import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SectionHead, Reveal } from "./Reveal";
import { BFSI_SEGMENTS, STARTUP_SEGMENTS } from "../content";

const Panel = ({ img, alt, kicker, title, lead, items, testid, clip }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <div ref={ref} className="group flex flex-col" data-testid={testid}>
      <div className={`relative aspect-[4/3] overflow-hidden border border-white/10 ${clip}`}>
        <motion.img src={img} alt={alt} style={{ y }} className="absolute inset-0 -top-[12%] h-[124%] w-full object-cover grayscale-[40%] transition-[filter] duration-700 group-hover:grayscale-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0c0e]/90 via-transparent to-transparent" />
        <p className="absolute left-6 bottom-5 eyebrow">{kicker}</p>
      </div>
      <h3 className="font-display mt-8 text-4xl sm:text-5xl leading-none text-zinc-100">{title}</h3>
      <p className="mt-4 max-w-lg text-zinc-400 leading-relaxed">{lead}</p>
      <ul className="mt-8 border-t border-white/10">
        {items.map((it) => (
          <li key={it} className="group/li flex items-center justify-between gap-4 border-b border-white/10 py-4 text-[15px] text-zinc-200">
            <span className="transition-transform duration-500 group-hover/li:translate-x-2">{it}</span>
            <ArrowRight className="h-4 w-4 shrink-0 text-emerald-400 opacity-0 -translate-x-2 transition-[opacity,transform] duration-500 group-hover/li:opacity-100 group-hover/li:translate-x-0" />
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Focus = () => (
  <section id="focus" className="relative py-28 md:py-40" data-testid="focus-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
      <SectionHead
        index="03"
        label="Where we specialise"
        title={<>Two worlds. <em className="text-emerald-400">One standard</em> of leadership.</>}
        sub="Regulated institutions need leaders who respect the rulebook. Startups need leaders who can write the next one. We search for both, with the same rigour."
      />
      <div className="mt-20 grid grid-cols-1 gap-20 lg:grid-cols-2 lg:gap-12">
        <Reveal>
          <Panel
            testid="focus-bfsi"
            img="/images/skyline.jpg"
            alt="City financial district skyline at night"
            kicker="Banking · Financial services · Insurance"
            title="BFSI"
            lead="Leaders who can grow books, manage risk and lead through regulation and digital transformation. Our home turf, backed by 25+ years inside the industry."
            items={BFSI_SEGMENTS}
            clip="rounded-tl-[120px] rounded-br-md rounded-tr-md rounded-bl-md"
          />
        </Reveal>
        <Reveal delay={0.12} className="lg:mt-32">
          <Panel
            testid="focus-startups"
            img="/images/startup.jpg"
            alt="Startup team meeting in a glass-walled office"
            kicker="Venture-backed · Fintech · Growth stage"
            title="Startups"
            lead="Founders hire their first leaders once. We help you make it count, with candidates who bring institutional discipline and startup speed."
            items={STARTUP_SEGMENTS}
            clip="rounded-tr-[120px] rounded-bl-md rounded-tl-md rounded-br-md"
          />
        </Reveal>
      </div>
    </div>
  </section>
);
